import { useMemo, useState } from "react";
import { arrayOf, bool, func, node, number, oneOfType, shape, string } from "prop-types";
import { Autocomplete } from "./index";
import { Grid, IconButton, Tooltip } from "@material-ui/core";
import { Search } from "@assets";
import { Close } from "@material-ui/icons";
import classnames from "classnames";
import { observer } from "mobx-react";

export const PhraseSearchBar = observer(({
  onAdd,
  onRemove,
  onClear,
  tooltipTitle,
  useTooltip,
  disabled,
  placeholder,
  className,
  buttonClassName,
  children,
  searchPhrases = [],
  limit = 1,
}) => {
  const [ active, setActive ] = useState(false);
  
  const selectedOptions = searchPhrases?.map(phrase => phrase.id) || [];
  
  const disableTooltip = useMemo(() => (
    disabled || !useTooltip || !tooltipTitle
  ), [ disabled, tooltipTitle, useTooltip ]);
  
  const tooltipText = useMemo(() => {
    if(!tooltipTitle)
      return "";
    if(typeof tooltipTitle === "string")
      return tooltipTitle;
    
    return active ? tooltipTitle.close : tooltipTitle.open;
  }, [ tooltipTitle, active ]);
  
  const handleSearchOpen = () => {
    if(disabled) return;
    
    if(active) onClear?.();
    
    setActive(!active);
  }
  
  const handleSearchPhraseRemove = (list, value, id) => {
    if(id)
      onRemove(id);
  }
  
  return (
    <Grid
      item container
      alignItems="center"
      wrap="nowrap"
      className={ className }
    >
      {
        active &&
        <Autocomplete
          multiple
          autoFocus
          idKey="id"
          labelKey="value"
          size="small"
          className="w-256 bg-secondary rounded-2"
          fieldClassName="mb-0"
          limitTags={limit}
          value={selectedOptions}
          options={searchPhrases}
          onOptionCreate={onAdd}
          onChange={handleSearchPhraseRemove}
          forcePopupIcon={false}
          disabled={disabled || searchPhrases?.length >= 1}
          formatNewOption={(v) => v}
          inputPlaceholder={placeholder}
        />
      }
      <Tooltip
        title={tooltipText}
        disableHoverListener={disableTooltip}
        disableFocusListener={disableTooltip}
        disableTouchListener={disableTooltip}
      >
        <IconButton
          onClick={handleSearchOpen}
          className={classnames( active && "ml-2", buttonClassName )}
          color="primary"
        >
          {
            active ? <Close /> : <Search />
          }
        </IconButton>
      </Tooltip>
      { children }
    </Grid>
  )
})

PhraseSearchBar.propTypes = {
  searchPhrases: arrayOf(shape({
    id: oneOfType([number, string]).isRequired,
    value: string.isRequired,
  })),
  onAdd: func.isRequired,
  onRemove: func.isRequired,
  onClear: func,
  tooltipTitle: oneOfType([
    string,
    shape({
      open: string.isRequired,
      close: string.isRequired,
    })
  ]),
  useTooltip: bool,
  disabled: bool,
  placeholder: string,
  className: string,
  buttonClassName: string,
  children: node,
  limit: number,
}